/**
 * xelara-header.js — BUILD-ORDER C5
 * Once the hero leaves the viewport the header gains .header--solid (and
 * .is-solid for the prototype's .p-header). Also writes --xel-header-h on
 * :root for sticky offsets. Vanilla ESM, no deps.
 *
 * Uses a scroll listener (not IntersectionObserver) because the nav lives
 * inside the hero: observing an ancestor that contains a soon-to-be-fixed
 * child is unreliable across engines, and programmatic scroll in some
 * embedded browsers only updates IO after long delays.
 */
function measure(header) {
  const h = Math.round(header.getBoundingClientRect().height);
  if (h > 0) document.documentElement.style.setProperty('--xel-header-h', `${h}px`);
}

function sync(header, hero) {
  if (!hero) {
    header.classList.add('header--solid', 'is-solid');
    return;
  }
  // Solid once the hero's bottom edge clears the header band.
  const solid = hero.getBoundingClientRect().bottom <= header.offsetHeight;
  header.classList.toggle('header--solid', solid);
  header.classList.toggle('is-solid', solid);
}

export function init(scope = document) {
  const header =
    scope.querySelector('[data-xel-header]') ||
    scope.querySelector('.p-header') ||
    scope.querySelector('.header');
  if (!header) return;

  const hero =
    scope.querySelector('[data-xel-hero]') ||
    scope.querySelector('.p-hero') ||
    scope.querySelector('#MainContent .banner') ||
    scope.querySelector('.xelara-hero');

  measure(header);
  sync(header, hero);

  // Two getBoundingClientRect calls per event: cheap enough to run inline,
  // and unlike requestAnimationFrame it also runs while the tab is hidden
  // (theme-editor previews, background tabs).
  const onScrollOrResize = () => {
    measure(header);
    sync(header, hero);
  };

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
  // Theme editor and `theme dev` hot reload re-render a section's DOM without
  // re-running module scripts; re-initialise inside the replaced section.
  document.addEventListener('shopify:section:load', (e) => init(e.target));
}

/* -- hover-open for the desktop dropdown panels ----------------------------
   Dawn opens <details> on click (details-disclosure.js). On pointer devices
   the panel also opens on hover and closes when the pointer leaves the item
   (150 ms grace so moving diagonally into the panel does not close it). */
function initHoverMenus(scope = document) {
  if (!window.matchMedia('(hover: hover)').matches) return;
  scope.querySelectorAll('header-menu > details.xel-menu').forEach((details) => {
    if (details.dataset.xelHover) return;
    details.dataset.xelHover = '1';
    const item = details.parentElement;
    let timer = 0;
    const summary = details.querySelector('summary');
    item.addEventListener('mouseenter', () => {
      clearTimeout(timer);
      if (!details.open) {
        details.open = true;
        details.dataset.xelHoverOpen = '1';
        summary?.setAttribute('aria-expanded', 'true');
      }
    });
    // A click on a hover-opened item must not toggle it shut again.
    summary?.addEventListener('click', (e) => {
      if (details.open && details.dataset.xelHoverOpen) {
        e.preventDefault();
        delete details.dataset.xelHoverOpen;
      }
    });
    item.addEventListener('mouseleave', () => {
      timer = setTimeout(() => {
        delete details.dataset.xelHoverOpen;
        details.open = false;
        details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      }, 150);
    });
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initHoverMenus(), { once: true });
  } else {
    initHoverMenus();
  }
  document.addEventListener('shopify:section:load', (e) => initHoverMenus(e.target));
}
